import React, { FC, useCallback, useState } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';
import { Loader } from '@eika/loader';
import { RadioButtonPanels } from '@eika/radio-button';

import { nonBlockedAccountsSelector, useAccounts } from '../../fetchHooks/useAccount';
import useFavoriteAccount, { useFavoriteAccountMutate } from '../../fetchHooks/useFavoriteAccount';
import useTranslation from '../../hooks/useTranslation';
import ActionResultInfo from '../shared/ActionResultInfo/ActionResultInfo';
import { StickyFooterRow } from '../shared/StickyFooter';

import './FavoriteAccountModal.scss';

interface Props {
    onCompleted: () => void;
}

const FavoriteAccountModalBody: FC<Props> = ({ onCompleted }) => {
    const translated = useTranslation();
    const { data: accounts, ...accountState } = useAccounts({
        select: nonBlockedAccountsSelector,
    });

    const [selectedAccount, setSelectedAccount] = useState<string | undefined>();

    const {
        data: favoriteAccount,
        isLoading,
        error,
    } = useFavoriteAccount({
        onSuccess: (data) => setSelectedAccount(data?.value),
    });
    const mutateFavAccount = useFavoriteAccountMutate();

    const onSubmit = useCallback(async () => {
        if (selectedAccount) {
            mutateFavAccount.mutateAsync({ value: selectedAccount }).then(onCompleted);
        }
    }, [mutateFavAccount, onCompleted, selectedAccount]);

    if (accountState.isLoading || isLoading) {
        return (
            <div className="action-result-centered">
                <Loader />
            </div>
        );
    }

    if (mutateFavAccount.isSuccess) {
        return (
            <div className="action-result-centered">
                <ActionResultInfo
                    title={translated.favoriteAccount.favoriteAccountSaved}
                    retry={onSubmit}
                    error={error ?? undefined}
                    icon={
                        <CheckmarkAnimation
                            className="transfer-success-checkmark"
                            checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
                        />
                    }
                />
            </div>
        );
    }

    const withOneAccountAlreadyAssigned = accounts && accounts.length === 1 && favoriteAccount;

    return (
        <>
            <div className="favorite-account-modal__body">
                {accounts && (
                    <form onSubmit={onSubmit}>
                        <RadioButtonPanels
                            label={translated.favoriteAccount.favoriteAccountModalTitle}
                            sublabel={translated.favoriteAccount.favoriteAccountModalDescription}
                            onChange={setSelectedAccount}
                            options={accounts.map((account) => {
                                return {
                                    value: account.accountNumber.value,
                                    label: account.alias,
                                    description: formatAccountNumber(account.accountNumber),
                                };
                            })}
                            selectedOption={selectedAccount}
                        />
                    </form>
                )}
                {withOneAccountAlreadyAssigned && (
                    <ErrorLabel
                        icon={IconTypes.UTROPSTEGN_24}
                        text={translated.settingsAccountFavoriteNeedMoreAccounts}
                        className="favorite-account-modal__warning"
                    />
                )}
                {selectedAccount && selectedAccount !== favoriteAccount?.value && (
                    <StickyFooterRow>
                        <ButtonWithSpinner
                            buttonType="positive"
                            size="small"
                            onClick={onSubmit}
                            showSpinner={mutateFavAccount.isPending}
                        >
                            {translated.favoriteAccount.saveFavoriteAccountButtonText}
                        </ButtonWithSpinner>
                    </StickyFooterRow>
                )}
            </div>
        </>
    );
};

export default FavoriteAccountModalBody;
